/**
 * Login page — PG Advogados corporate sign-in.
 *
 * Single action: Microsoft 365 SSO via Firebase Auth. On success the auth state
 * change re-runs the router guards; we also navigate explicitly to the intended
 * destination (`?redirect=`), defaulting to the chat home.
 *
 * Layout: a two-column brand split on desktop — a dark panel carrying the
 * "connecting minds" graphism and the firm tagline, beside the sign-in card. On
 * small screens the brand panel collapses and only the card (with a compact
 * wordmark) remains. Dark-first, accessible, no emoji (brand voice).
 */
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useState } from 'react'
import { toast } from 'sonner'

import { Logo, Monogram } from '@/components/brand/logo'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth'

/** Microsoft logo mark (inline SVG so it works under the artifact/asset CSP). */
function MicrosoftMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 21 21" className="size-4" focusable="false">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  )
}

export function LoginRoute() {
  const { signInWithMicrosoft } = useAuth()
  const { redirect } = useSearch({ from: '/login' })
  const navigate = useNavigate()
  const [pending, setPending] = useState(false)

  async function handleSignIn() {
    setPending(true)
    try {
      await signInWithMicrosoft()
      // Move to the intended destination. The /login guard also redirects once the
      // auth state settles, so this is belt-and-suspenders for a snappy transition.
      await navigate({ to: redirect ?? '/' })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Falha ao entrar. Tente novamente.'
      toast.error('Não foi possível entrar', { description: message })
      setPending(false)
    }
  }

  return (
    <div className="bg-background text-foreground grid min-h-svh lg:grid-cols-2">
      {/* Brand panel — dark, with the "connecting minds" graphism as a subtle motif */}
      <aside className="relative hidden overflow-hidden bg-[#171a28] p-10 lg:flex lg:flex-col xl:p-14">
        {/* Graphism motif, low-opacity, bottom-anchored so it never fights the copy */}
        <img
          src="/brand/pg-graphism.svg"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 bottom-0 w-full select-none opacity-40"
        />
        {/* Soft top-down fade so the wordmark stays crisp over the artwork */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-linear-to-b from-[#171a28] via-[#171a28]/70 to-transparent"
        />

        <div className="relative flex items-center gap-3">
          <Monogram size={40} />
          <Logo className="h-7" />
        </div>

        <div className="relative mt-auto max-w-md">
          <p className="text-brand font-mono text-xs tracking-[0.18em] uppercase">
            Agente executivo
          </p>
          <p className="text-foreground mt-4 font-display text-3xl leading-tight font-semibold tracking-tight text-balance xl:text-4xl">
            O amanhã do direito, disponível para você hoje.
          </p>
          <p className="text-muted-foreground mt-4 max-w-sm text-sm leading-relaxed">
            Sua central de apoio ao dia a dia — agenda, reuniões, clientes e lembretes, sempre à
            mão.
          </p>
        </div>
      </aside>

      {/* Sign-in column */}
      <main className="flex flex-col items-center justify-center gap-8 p-6 md:p-10">
        <div className="flex w-full max-w-sm flex-col gap-8">
          {/* Compact brand for small screens where the panel is hidden */}
          <div className="flex flex-col items-center gap-3 text-center lg:hidden">
            <Monogram size={44} />
            <Logo className="h-6" />
          </div>

          <div className="flex flex-col gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">Bem-vindo de volta</h1>
            <p className="text-muted-foreground text-sm text-balance">
              Entre com sua conta corporativa Microsoft 365 (@pgadvogados.com.br) para continuar.
            </p>
          </div>

          <div className="flex flex-col gap-4">
            <Button
              type="button"
              className="w-full shadow-sm"
              onClick={handleSignIn}
              disabled={pending}
            >
              <MicrosoftMark />
              {pending ? 'Entrando…' : 'Entrar com a Microsoft'}
            </Button>
            <p className="text-muted-foreground text-center text-xs text-balance">
              Ao entrar, você concorda com o uso confidencial das informações do escritório.
            </p>
          </div>
        </div>
      </main>
    </div>
  )
}

export default LoginRoute
